import React, { FC, useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core';
import { createUseStyles } from '../../../../theming';
import { CollaborativeSource, CreateNoteOptions } from '../../../../interfaces/api/Collaborative';
import { Controller, useForm } from 'react-hook-form';

const useStyles = createUseStyles({
  title: {
    width: 400,
    '& h2': {
      color: 'rgba(0, 0, 0, 0.87)',
    },
  },
  error: {
    color: '#f44336',
    fontSize: '.75rem',
    padding: '0 25px',
  },
  formControl: {
    marginBottom: '16px !important',
  },
});

const defaultFormValues = {
  title: '',
  text: '',
};

interface Props {
  noteSource?: CollaborativeSource;
  open: boolean;
  onClose: () => void;
  onValidate: (createNoteOptions: CreateNoteOptions) => void;
}

const AddNoteDialog: FC<Props> = ({ open, noteSource, onClose, onValidate }) => {
  const classes = useStyles();

  const [formError, setFormError] = useState<string | undefined>();
  const {
    reset,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<CreateNoteOptions>({
    defaultValues: {
      ...defaultFormValues,
      attachedTo: noteSource,
    },
  });

  useEffect(() => {
    setFormError(undefined);
  }, []);

  const onSubmit = async (createNoteOptions: CreateNoteOptions) => {
    try {
      setFormError(undefined);
      await onValidate(createNoteOptions);
      reset(defaultFormValues);
      onClose();
    } catch (e) {
      setFormError(e.message);
    }
  };

  return (
    <Dialog onClose={onClose} scroll='paper' open={open}>
      <DialogTitle className={classes.title}>Créer une note</DialogTitle>

      <DialogContent>
        <Controller
          name='title'
          control={control}
          rules={{ required: { value: true, message: 'Le champ est requis' } }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              className={classes.formControl}
              label='Titre'
              placeholder='Retrouver la note plus tard'
              variant='outlined'
              fullWidth
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name='text'
          control={control}
          rules={{ required: { value: true, message: 'Le champ est requis' } }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              className={classes.formControl}
              label='Texte'
              placeholder='Contenu de la note'
              variant='outlined'
              multiline
              rows={3}
              fullWidth
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />
      </DialogContent>

      <DialogActions>
        <Button type='button' onClick={onClose} color='primary'>
          Annuler
        </Button>

        <Button type='submit' color='primary' disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
          Créer
        </Button>
      </DialogActions>

      {formError && <p className={classes.error}>{formError}</p>}
    </Dialog>
  );
};

export default AddNoteDialog;
